import {
  AppCard,
  Icon,
  InfoField,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import {
  mdiAccount,
  mdiCalendarBlank,
  mdiFileDocumentOutline,
  mdiFileDocumentRefreshOutline,
  mdiFileSign,
} from "@mdi/js";
import { Box } from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Term } from "../../../../../@types/services/Term";
import type { TermDocument } from "../../../../../@types/services/TermDocument";
import { UserInfoBadge } from "../../../../../components/molecules/UserInfoBadge";
import { ForbiddenError } from "../../../../../errors/ForbiddenError";
import {
  fetchActiveTermDocument,
  fetchTerm,
} from "../../../../../services/terms";
import { i18nextParseToDate } from "../../../../../utils/i18nextParseTo";
import { EditTermDocumentForm } from "./-internal_components/EditTermDocumentForm";
import { EditTermForm } from "./-internal_components/EditTermForm";

const EditTerm: React.FC = () => {
  const { t } = useTranslation();

  const term = Route.useLoaderData();

  const [termDocument, setTermDocument] = useState<TermDocument>();

  const handleFetchTermDocument = useCallback(async (termId: Term["id"]) => {
    fetchActiveTermDocument(termId).then((data) => setTermDocument(data));
  }, []);

  useEffect(() => {
    handleFetchTermDocument(term.id);
  }, [handleFetchTermDocument, term.id]);

  return (
    <PageContainer showBackButton title={term.name}>
      <AppCard icon={<Icon icon={mdiFileSign} />} title={t("terms.updateTerm")}>
        <EditTermForm term={term} />
      </AppCard>

      <AppCard
        icon={<Icon icon={mdiFileDocumentRefreshOutline} />}
        title={t("terms.updateDocument")}
      >
        <EditTermDocumentForm termId={term.id} />
      </AppCard>

      {!!termDocument && (
        <AppCard
          icon={<Icon icon={mdiFileDocumentOutline} />}
          title={t("terms.currentDocument")}
        >
          <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: "1fr 1fr" }}>
            <InfoField label={t("common.createdBy")}>
              <UserInfoBadge
                avatarIcon={<Icon icon={mdiAccount} />}
                avatarType="icon"
                info={termDocument.uploaded_by.full_name}
                photoUser={termDocument.uploaded_by}
                subInfo={termDocument.uploaded_by.login}
              />
            </InfoField>

            <InfoField
              label={t("common.createdAt")}
              labelIconPath={mdiCalendarBlank}
              text={t(
                "intlDateTime",
                i18nextParseToDate(termDocument.created_at),
              )}
            />
          </Box>

          <InfoField
            label={t("common.justification")}
            text={termDocument.justification}
            variant="boxed"
          />

          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              flexDirection: "column",
              height: "100%",
              justifyContent: "center",
            }}
          >
            <InfoField fullWidth label={t("terms.form.filePtBr")}>
              <iframe
                height="800px"
                loading="lazy"
                src={termDocument.attachment_pt_br.url_inline}
                title={termDocument.attachment_pt_br.filename}
                width="100%"
              />
            </InfoField>
          </Box>

          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              flexDirection: "column",
              height: "100%",
              justifyContent: "center",
            }}
          >
            <InfoField fullWidth label={t("terms.form.fileEn")}>
              <iframe
                height="800px"
                loading="lazy"
                src={termDocument.attachment_en.url_inline}
                title={termDocument.attachment_en.filename}
                width="100%"
              />
            </InfoField>
          </Box>
        </AppCard>
      )}
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/vsupplierregistration/terms/$termId/edit",
)({
  beforeLoad({ context }) {
    if (context.auth.user?.roles_names.includes("Term - Update")) {
      return;
    }

    throw new ForbiddenError();
  },
  component: EditTerm,
  loader: ({ params: { termId } }) => fetchTerm(termId),
});
